import { gsap } from "gsap";

import { ScrollTrigger } from "gsap/all";
import React from "react";

// Register plugin once
gsap.registerPlugin(ScrollTrigger);

// Add small-screen detection utility
export const isSmallScreen = (maxWidth = 768) =>
  typeof window !== "undefined" &&
  window.matchMedia(`(max-width: ${maxWidth}px)`).matches;

// Utility function to split text into spans
export const splitTextIntoSpans = (text, splitBy = " ") => {
  return text
    .split(splitBy)
    .map((part, idx) =>
      React.createElement(
        "span",
        { key: idx, style: { willChange: "transform, opacity, filter" } },
        splitBy === " " ? part + " " : part
      )
    );
};

// Utility function for text reveal animation
export const animateTextReveal = (element, options = {}) => {
  const {
    trigger = element,
    start = "top 100%",

    duration = 0.75,
    stagger = 0.03,
    delay = 0,
    ease = "power1.inOut",
    isMobile = undefined, // allow undefined so we can auto-detect
    markers = false,

    ...otherOptions
  } = options;

  if (!element) return;

  // final mobile decision: explicit option overrides auto-detection
  const mobile = typeof isMobile === "boolean" ? isMobile : isSmallScreen();

  const spans = element.querySelectorAll("span");

  return gsap.fromTo(
    spans,
    {
      x: "-100%",
      opacity: 0,
      // don't use blur on small screens
      filter: mobile ? "none" : "blur(10px)",
    },
    {
      x: "0%",
      opacity: 1,
      // avoid animating blur on mobile
      filter: mobile ? "none" : "blur(0px)",
      duration: mobile ? duration * 0.6 : duration,
      ease,
      stagger: mobile ? stagger * 0.5 : stagger,
      delay,
      scrollTrigger: {
        trigger,
        start,
        toggleActions: "play none none reverse",
        refreshPriority: -1,
        markers, // Enable markers for debugging
        preventOverlaps: true,
        fastScrollEnd: true,
      },
    }
  );
};

// Add new maskAnimation function
export const maskAnimation = (element, options = {}) => {
  const {
    trigger = element,
    start = "top 100%",
    duration = 0.75,
    stagger = 0.03,
    delay = 0,
    ease = "power1.inOut",
    isMobile = false,
    markers = false,
    ...otherOptions
  } = options;

  if (!element) return;

  const spans = element.querySelectorAll("span");

  return gsap.fromTo(
    spans,
    {
      x: "-100%",
      opacity: 0,
      // start mask off to the right (matches your CSS initial 40% 0)
      webkitMaskPosition: "40% 0%",
      maskPosition: "40% 0%",
      webkitMaskSize: "800% 100%",
      maskSize: "800% 100%",
      webkitMaskImage:
        "linear-gradient(45deg, #000, #000 15%, transparent 30%)",
      maskImage: "linear-gradient(45deg, #000, #000 15%, transparent 30%)",
      webkitMaskMode: "match-source",
      maskMode: "match-source",
      webkitMaskOrigin: "border-box",
      maskOrigin: "border-box",
      webkitMaskRepeat: "revert",
      maskRepeat: "revert",
    },
    {
      x: "0%",
      opacity: 1,
      webkitMaskPosition: "0% 0%",
      maskPosition: "0% 0%",
      // animate mask to final position 0% 0%

      // ensure final inline CSS has no transition after animation completes
      onComplete: () => {
        spans.forEach((s) => {
          s.style.webkitMaskPosition = "0% 0%";
          s.style.maskPosition = "0% 0%";
          s.style.transition = "none";
        });
      },
      duration: isMobile ? duration * 0.6 : duration,
      ease,
      stagger: isMobile ? stagger * 0.5 : stagger,
      delay,
      scrollTrigger: {
        trigger,
        start,
        toggleActions: "play none none reverse",
        refreshPriority: -1,
        markers, // Enable markers for debugging
        preventOverlaps: true,
        fastScrollEnd: true,
      },
    }
  );
};

// Utility function for card popup animation
export const animateCardPopup = (cardElements) => {
  if (!cardElements) return;

  cardElements.forEach((cardEl) => {
    if (cardEl) {
      gsap.fromTo(
        cardEl,
        {
          transform:
            "translate3d(0px, 0px, -0.0001px) rotateX(-45deg) scale(0.75, 0.75)",
          opacity: 0,
        },
        {
          transform: "translate3d(0px, 0px, 0px) rotateX(0deg) scale(1, 1)",
          opacity: 1,
          duration: 0.8,
          ease: "power2.out",
          scrollTrigger: {
            trigger: cardEl,
            start: "top 110%",
            toggleActions: "play none none reverse",
            preventOverlaps: true,
            fastScrollEnd: true,
          },
        }
      );
    }
  });
};
